
import React from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import CTAButton from '@/components/CTAButton';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { AlertCircle, Star, Gift, Sparkles } from 'lucide-react';

const OTOHero = () => {
  const { isMobile } = useBreakpoint();

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/10 to-white" 
      paddingY="py-8 md:py-12 lg:py-16 xl:py-24" 
      containerWidth="wide" 
      enableMagicBackground={true} 
      patternColor="#003a70" 
      backgroundPattern="none" 
      patternOpacity={3} 
      decorativeElements={
        <>
          <div className="absolute top-1/4 left-1/6 w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 bg-[#FEE6D8]/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/3 right-1/6 w-56 h-56 md:w-72 md:h-72 lg:w-96 lg:h-96 bg-[#003a70]/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-2/3 left-1/2 w-40 h-40 md:w-48 md:h-48 lg:w-64 lg:h-64 bg-gradient-to-r from-[#FEE6D8]/15 to-[#003a70]/5 rounded-full blur-2xl animate-float" style={{ animationDelay: '4s' }}></div>
        </>
      }
    >
      <div className="text-center space-y-6 md:space-y-8 lg:space-y-12 px-4">
        
        {/* Header/Hook */}
        <div className="space-y-3 md:space-y-4 animate-fade-in">
          <div className="inline-flex items-center gap-2 md:gap-3 lg:gap-4 px-4 md:px-6 lg:px-8 py-2 md:py-3 lg:py-4 bg-gradient-to-r from-red-100 to-red-50 rounded-full shadow-lg border-2 border-red-200 hover:shadow-xl transition-all duration-300 hover:scale-105">
            <AlertCircle className="text-red-600" size={isMobile ? 20 : 28} />
            <span className="text-red-600 font-semibold text-sm md:text-lg lg:text-xl xl:text-2xl">STOP! Your Order Is Not Complete Yet...</span>
          </div>
          
          <h1 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl" : "text-3xl md:text-4xl lg:text-5xl xl:text-6xl"
          )}>
            Get Everything You Need to Start Selling
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              AI Business Cards TODAY
            </span>
          </h1>
        </div>

        {/* Subheadline */}
        <div className="space-y-3 md:space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="relative bg-gradient-to-br from-[#FEE6D8] via-[#FEE6D8]/90 to-[#FEE6D8]/70 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 xl:p-12 border-2 md:border-4 border-[#003a70]/20 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 overflow-hidden group max-w-4xl mx-auto">
            
            {/* Sparkle effects */}
            <div className="absolute top-3 right-4 md:top-4 md:right-6 animate-pulse">
              <Sparkles className="text-[#003a70] opacity-60" size={isMobile ? 16 : 24} />
            </div>
            <div className="absolute bottom-4 left-4 md:bottom-6 md:left-8 animate-pulse" style={{ animationDelay: '1s' }}>
              <Sparkles className="text-[#003a70] opacity-40" size={isMobile ? 14 : 20} />
            </div>
            
            {/* Content wrapper */}
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center animate-pulse border-2 md:border-4 border-white shadow-lg">
                  <Gift className="text-white" size={isMobile ? 20 : 28} />
                </div>
                <div className="flex gap-1">
                  {[...Array(3)].map((_, i) => (
                    <div 
                      key={i} 
                      className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#003a70] rounded-full animate-bounce" 
                      style={{ animationDelay: `${i * 0.2}s` }}
                    ></div>
                  ))}
                </div>
              </div>
              
              <h2 className={cn(
                "text-[#003a70] font-bold leading-tight mb-3 md:mb-4",
                isMobile ? "text-xl" : "text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
              )}>
                Complete Done-For-You Package
              </h2>
              
              <div className="inline-block bg-gradient-to-r from-[#003a70] to-[#003a70]/90 text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-bold text-sm md:text-lg lg:text-xl shadow-lg mb-4 md:mb-6 animate-pulse">
                Available Only On This Page
              </div>
              
              <div className="flex justify-center items-center gap-2 md:gap-3 mt-4 md:mt-6">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className="text-[#003a70] fill-[#003a70] animate-pulse drop-shadow-lg" 
                    style={{ animationDelay: `${i * 0.15}s` }} 
                    size={isMobile ? 16 : 24} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* VSL Placeholder */}
        <div className="max-w-4xl mx-auto animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#003a70]/5 to-[#FEE6D8]/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: '0' }}>
              <iframe 
                src="https://www.loom.com/embed/c4da8df85276472bb65e5456f7eb7927?sid=ddcb1704-fd85-4725-a13d-313176c8290c&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true" 
                frameBorder="0" 
                allowFullScreen 
                style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }}
              ></iframe>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default OTOHero;
